import React, { useState, useEffect } from "react";
import { Trash2, Lock, ShieldCheck, AlertTriangle, Archive } from "lucide-react";
import { Modal } from "./Modal";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * RemoveLineModal — Manager/Admin only. Removes a mis-received equipment
 * line entirely (e.g. a duplicate captured at receiving, or a whole item
 * that shouldn't have been on this job). Fetches a removal-preview first so
 * the choice between "Archive (recoverable)" and "Permanently delete" is
 * made with full knowledge of any linked equipment's other history.
 */
export function RemoveLineModal({ item, onClose, onRemoved }) {
  const { user } = useAuth();
  const isManager = user?.role === "manager" || user?.role === "admin";
  const [preview, setPreview] = useState(null);
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(null); // "archive" | "permanent" | null

  useEffect(() => {
    api.get(`/job-items/${item.id}/removal-preview`)
      .then((r) => setPreview(r.data))
      .catch((e) => { toast.error(apiErr(e)); onClose && onClose(); });
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const disabled = preview?.locked && !(override && isManager);

  const remove = async (mode) => {
    if (mode === "permanent" && !window.confirm(
      "Permanently delete this line? This cannot be undone." +
      (preview?.equipment_id && !preview?.equipment_is_exclusive
        ? "\n\nNote: the linked equipment record has other history and will be archived, not deleted, to protect it."
        : preview?.equipment_id ? "\n\nThe linked equipment register record will also be permanently deleted." : "")
    )) return;
    setBusy(mode);
    try {
      const params = { mode };
      if (preview?.locked && override) params.force = true;
      const r = await api.delete(`/job-items/${item.id}`, { params });
      toast.success(
        mode === "permanent" ? "Line permanently deleted" : "Line archived — fully recoverable"
      );
      if (r.data.equipment_protected_reason) toast.info(r.data.equipment_protected_reason);
      onRemoved && onRemoved();
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };

  return (
    <Modal open onClose={onClose} icon={Trash2}
      title="Remove equipment line"
      subtitle={`${item.description || ""} · ${item.serial_number ? "SN " + item.serial_number : item.issue_number ? "Issue " + item.issue_number : "—"}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={!!busy}>Cancel</button>
        <button className="btn ghost" onClick={() => remove("archive")} disabled={!!busy || disabled}
          data-testid="remove-line-archive">
          <Archive size={13} /> {busy === "archive" ? "Archiving…" : "Archive (recoverable)"}
        </button>
        <button className="btn danger" onClick={() => remove("permanent")} disabled={!!busy || disabled}
          data-testid="remove-line-permanent">
          <Trash2 size={13} /> {busy === "permanent" ? "Deleting…" : "Permanently delete"}
        </button>
      </>}>

      {!preview && <div className="t-mut" style={{ padding: 20, textAlign: "center" }}>Checking line history…</div>}

      {preview && (
        <>
          {preview.locked && (
            <div className="card pad" data-testid="remove-line-locked"
              style={{ marginBottom: 12, background: override ? "#e8f5e9" : "#fdecea",
                       border: `1px solid ${override ? "#a5d6a7" : "#f3c7c2"}`,
                       display: "flex", gap: 10, alignItems: "flex-start" }}>
              {override ? <ShieldCheck size={16} style={{ color: "#2e7d32", marginTop: 1 }} />
                        : <Lock size={16} style={{ color: "var(--red)", marginTop: 1 }} />}
              <div style={{ flex: 1, fontSize: 12.5 }}>
                <div className="t-strong">{override ? "Manager override active" : `Locked — ${preview.lock_reason}`}</div>
                <div className="t-mut" style={{ marginTop: 2 }}>
                  {override ? "Removing a locked line is audit-logged and may affect traceability."
                            : "Only a manager can override to remove a locked line."}
                </div>
              </div>
              {isManager && (
                <button className={`btn sm ${override ? "ghost" : "gold"}`} onClick={() => setOverride((v) => !v)}
                  data-testid="remove-line-override-toggle">
                  {override ? "Disable override" : "Override lock"}
                </button>
              )}
            </div>
          )}

          <div className="notice" style={{ marginBottom: 10 }}>
            <AlertTriangle size={14} />
            <span>
              {preview.equipment_id ? (
                preview.equipment_is_exclusive
                  ? "This item's equipment register record is only used by this one line — safe to permanently delete both, or archive both (recoverable)."
                  : `This item's equipment register record has other history: ${preview.other_job_items_count} other job(s), ${preview.certs_count} certificate(s), ${preview.events_count} calibration event(s). Permanently deleting the line will keep that equipment record — archived instead of destroyed — to protect that history.`
              ) : "This line has no linked equipment register record."}
            </span>
          </div>
          <p className="t-mut" style={{ fontSize: 12.5 }}>
            Archiving keeps a full recoverable copy in the deleted-records archive. Permanently deleting cannot be undone.
          </p>
        </>
      )}
    </Modal>
  );
}
